import { BusinessPlanType, Payments, Roles } from '@prisma/client';
import posthog from 'posthog-js';
import * as Sentry from '@sentry/react';
import API_URL from '../apiconfig';
import { Business, Employee, LoadedUser, User } from '../interfaces';
import { CreateCustomerValues } from '../utils/form/customerForm';
import axios from './api';
import client from '../utils/react-query/client';
import { CustomerOverview } from '../types/customers';

export async function getUser() {
  try {
    const user = await axios.get(`${API_URL}/v1/users/me`);

    return user.data.result;
  } catch (error) {
    throw new Error('Cannot get user', { cause: error });
  }
}

export async function getCustomTokenForUser(accessToken: string) {
  const customToken = await axios.get(`${API_URL}/v1/users/custom`, {
    headers: {
      authorization: `Bearer ${accessToken}`,
    },
  });

  return customToken.data.result as string | null;
}

export async function getCustomerById(customerId: string) {
  try {
    const customer = await axios.get(`${API_URL}/v1/customers/${customerId}`);

    return customer.data.result;
  } catch (error) {
    throw new Error('Cannot find customer', { cause: error });
  }
}

export async function getCustomerOverview(
  customerId: string,
  businessId?: string,
) {
  try {
    const customer = await axios.get(
      `${API_URL}/v1/customers/overview/${customerId}`,
      {
        params: {
          businessId,
          treatManagerAsEmployee: false,
        },
      },
    );

    return customer.data.result as CustomerOverview;
  } catch (error) {
    throw new Error('Cannot find customer', { cause: error });
  }
}

/**
 * We used to store selectedBusiness in sessionStorage.
 * To avoid current users from losing their selected business after the update,
 * get the selected business from sessionStorage if it exists, otherwise fallback to localStorage.
 * If sessionStorage value is found, unset it to avoid confusion in the future since we are standardizing on localStorage.
 */
export function getBackwardsCompatibleSelectedBusiness(): string {
  const localStorageBusiness = localStorage.getItem('selectedBusiness');
  if (localStorageBusiness) return localStorageBusiness;

  const sessionStorageBusiness = sessionStorage.getItem('selectedBusiness');
  if (!sessionStorageBusiness) return '';

  localStorage.setItem('selectedBusiness', sessionStorageBusiness);
  sessionStorage.removeItem('selectedBusiness');

  return sessionStorageBusiness;
}

export function createFullUser(
  user: User,
  businesses: Business[],
  employee: Employee,
  selectedBusinessId?: string,
) {
  const queryParamBusinessId = new URLSearchParams(window.location.search).get(
    'business',
  );

  const paramBusiness = queryParamBusinessId
    ? businesses.find(b => b.businessId === queryParamBusinessId)
    : undefined;

  const selectedBusiness = selectedBusinessId
    ? businesses.find(b => b.businessId === selectedBusinessId)
    : undefined;
  const primaryBusinessId = employee.primaryEmployeeToBusinessId
    ? employee.employeesToBusinesses?.find(
        e => e.id === employee.primaryEmployeeToBusinessId,
      )?.businessId
    : undefined;
  const primaryBusiness = primaryBusinessId
    ? businesses.find(b => b.businessId === primaryBusinessId)
    : undefined;
  const business =
    paramBusiness ?? selectedBusiness ?? primaryBusiness ?? businesses[0];
  const employeeToBusiness =
    employee.employeesToBusinesses?.find(
      e => e.businessId === business?.businessId,
    ) ?? employee.employeesToBusinesses?.[0];

  // Save selected business to session storage - could be possible that previous business is not found in the list of businesses
  localStorage.setItem('selectedBusiness', business.businessId);

  const entityType =
    business.type === BusinessPlanType.STUDIO ? 'business' : 'employee';
  const entityId =
    business.type === BusinessPlanType.STUDIO
      ? business.businessId
      : employee.employeeId;

  posthog.identify(user.userId);
  posthog.group(entityType, entityId);

  // Sentry identity: id only — never email or name.
  Sentry.setUser({ id: user.userId });
  Sentry.setTag('business_id', business.businessId);

  const canViewReports = !!employeeToBusiness?.canViewReports;
  const canManageTeam = !!employeeToBusiness?.canManageTeam;
  const canManageWaivers = !!employeeToBusiness?.canManageWaivers;
  const canIssueRefunds = !!employeeToBusiness?.canIssueRefunds;
  const canManageCampaigns = !!employeeToBusiness?.canManageCampaigns;
  const canViewAllShopCustomers = !!employeeToBusiness?.canViewAllShopCustomers;
  const generalRequestOwner = !!employeeToBusiness?.generalRequestOwner;

  const orderedServices = business.services?.sort((a, b) => {
    const aOrder = a.sortOrder ?? Number.MAX_SAFE_INTEGER;
    const bOrder = b.sortOrder ?? Number.MAX_SAFE_INTEGER;
    return aOrder - bOrder;
  });

  // When an admin is impersonating, present them as SHOP_MANAGER on the FE so
  // every `user.role === 'SHOP_MANAGER'` UI gate works. The JWT/auth header is
  // untouched, so the API still authorizes them as ADMIN.
  const effectiveRole =
    user.role === Roles.ADMIN && employeeToBusiness?.isImpersonation
      ? Roles.SHOP_MANAGER
      : user.role;

  const fullUser: LoadedUser = {
    ...user,
    role: effectiveRole,
    canViewReports,
    canManageTeam,
    canManageWaivers,
    canIssueRefunds,
    canManageCampaigns,
    canViewAllShopCustomers,
    generalRequestOwner,
    inactiveEmployeesToBusinesses: employee.inactiveEmployeesToBusinesses ?? [],
    restrictedBusinessIds: employee.restrictedBusinessIds ?? [],
    isImpersonation: employeeToBusiness?.isImpersonation,
    business: {
      id: business.businessId,
      organizationId: business.organizationId,
      name: business.name,
      urlPath: business.urlPath,
      coverImage: business.coverImage,
      thumbnailImage: business.thumbnailImage,
      city: business.city,
      state: business.state,
      zip: business.zip,
      streetAddress: business.streetAddress,
      streetAddress2: business.streetAddress2,
      employees: [],
      services: orderedServices,
      defaultTattooDeposit: business.defaultTattooDeposit,
      terminalReaderId: business.terminalReaderId,
      businessTaxRates: business.businessTaxRates,
      inStoreCustomerServiceFeeRate: business.inStoreCustomerServiceFeeRate,
      onlineCustomerServiceFeeRate: business.onlineCustomerServiceFeeRate,
      type: business.type,
      tattooAftercareLink: business.tattooAftercareLink,
      reviewLink: business.reviewLink,
      allowGeneralRequests: business.allowGeneralRequests,
      stationMax: business.stationMax,
      businessTrips: business.businessTrips,
      disableCreditTipping: business.disableCreditTipping,
      inactiveEmployeesToBusinesses: business.inactiveEmployeesToBusinesses,
      isTerminalPurchased: business.isTerminalPurchased,
      terminalStripeSubscriptionId: business.terminalStripeSubscriptionId,
      terminalSubscriptionPaymentFailed:
        business.terminalSubscriptionPaymentFailed,
      isBnplEnabled: business.isBnplEnabled,
      bnplFeePayer: business.bnplFeePayer,
      paymentsRate: business.paymentsRate,
      reviewLinksEnabled: business.reviewLinksEnabled,
      shopPolicyRaw: business.shopPolicyRaw,
      queueSettings: business.queueSettings,
      connectedStripeAt: business.connectedStripeAt,
      giftCardsEnabled: business.giftCardsEnabled,
      defaultGiftCardBalance: business.defaultGiftCardBalance,
      allowMinors: business.allowMinors,
      minimumTattooAgeInYears: business.minimumTattooAgeInYears,
      requireMinorIdentification: business.requireMinorIdentification,
      requireMinorBirthCertificate: business.requireMinorBirthCertificate,
      requireMinorGuardianIdentification:
        business.requireMinorGuardianIdentification,
      requireMinorGuardianPresent: business.requireMinorGuardianPresent,
      hasAdsFeature: business.hasAdsFeature,
      cashDiscountEnabled: business.cashDiscountEnabled,
      businessWebRedesignEligible: !!business.businessWebRedesignEligible,
      businessWebRedesignEnabled: !!business.businessWebRedesignEnabled,
      businessWebRedesignLocked: !!business.businessWebRedesignLocked,
      inventoryEnabled: !!business.inventoryEnabled,
    },
    allBusinesses: businesses,
  };

  if (business.employeesToBusinesses) {
    // Sort employees by sortOrder if it exists, otherwise add to end
    const sortedEmployees = [...business.employeesToBusinesses].sort((a, b) => {
      // If both have sortOrder, compare them
      if (a.sortOrder !== undefined && b.sortOrder !== undefined) {
        return a.sortOrder - b.sortOrder;
      }
      // If only a has sortOrder, it comes first
      if (a.sortOrder !== undefined) {
        return -1;
      }
      // If only b has sortOrder, it comes first
      if (b.sortOrder !== undefined) {
        return 1;
      }
      // If neither has sortOrder, maintain original order
      return 0;
    });

    sortedEmployees.forEach(employeeMapping => {
      fullUser.business.employees.push({
        payrollType: employeeMapping.payrollType,
        depositToEmployeePercent: employeeMapping.depositToEmployeePercent,
        tipToEmployeePercent: employeeMapping.tipToEmployeePercent,
        paymentToEmployeePercent: employeeMapping.paymentToEmployeePercent,
        isBookable: employeeMapping.isBookable,
        bonusCommissionPercent: employeeMapping.bonusCommissionPercent,
        addToAllChats: employeeMapping.addToAllChats,
        canViewReports: employeeMapping.canViewReports,
        canManageWaivers: employeeMapping.canManageWaivers,
        canIssueRefunds: employeeMapping.canIssueRefunds,
        canManageTeam: employeeMapping.canManageTeam,
        canManageCampaigns: employeeMapping.canManageCampaigns,
        canViewAllShopCustomers: employeeMapping.canViewAllShopCustomers,
        generalRequestOwner: employeeMapping.generalRequestOwner,
        payeeForOtherPayroll: employeeMapping.payeeForOtherPayroll,
        selectedRoles: employeeMapping.selectedRoles,
        color: employeeMapping.color,
        sortOrder: employeeMapping.sortOrder,
        defaultHourlyRate: employeeMapping.defaultHourlyRate,
        ...employeeMapping.employee,
      });
    });
  }

  return fullUser;
}

export async function addCustomerDatabase(
  values: CreateCustomerValues,
  businessId?: string,
) {
  // Filter out fields with empty string values
  const {
    streetAddress,
    streetAddress2,
    city,
    state,
    zip,
    ecFirstName,
    ecLastName,
    ecPhoneNumber,
    ecEmail,
    ...rest
  } = values;

  const filteredValues = Object.entries(rest).reduce(
    (acc: Partial<CreateCustomerValues>, [key, value]) => {
      if (value !== '') {
        acc[key as keyof CreateCustomerValues] = value;
      }
      return acc;
    },
    {} as Partial<CreateCustomerValues>,
  );

  const hasAddress = [streetAddress, city, state, zip].some(v => v);
  const address = hasAddress
    ? { streetAddress, streetAddress2, city, state, zip }
    : undefined;

  const hasEmergencyContact = [
    ecFirstName,
    ecLastName,
    ecPhoneNumber,
    ecEmail,
  ].some(v => v);
  const emergencyContact = hasEmergencyContact
    ? {
        ...(ecFirstName && { firstName: ecFirstName }),
        ...(ecLastName && { lastName: ecLastName }),
        ...(ecPhoneNumber && { phoneNumber: `+${ecPhoneNumber}` }),
        ...(ecEmail && { email: ecEmail }),
      }
    : undefined;

  const customer = await axios.post(`${API_URL}/v1/customers`, {
    ...filteredValues,
    phoneNumber: `+${values.phoneNumber}`,
    businessId,
    ...(address && { address }),
    ...(emergencyContact && { emergencyContact }),
  });

  return customer.data.result;
}

export async function getCustomerTransactions(
  customerId: string | undefined,
  businessId: string,
  cursor?: string,
): Promise<{ transactions: Payments[]; nextCursor: string | null }> {
  try {
    const response = await axios.get(
      `${API_URL}/v1/customers/transactions/${customerId}`,
      { params: { businessId, cursor } },
    );
    return {
      transactions: response.data.result as Payments[],
      nextCursor: (response.data.meta?.nextCursor as string | null) ?? null,
    };
  } catch (error: unknown) {
    // Check if the error is an Axios error with a response and a 404 status
    if (error instanceof Error) {
      throw error;
    } else {
      throw new Error('Unknown error occurred', { cause: error });
    }
  }
}

export async function addCustomerAlgolia(
  customerId: string,
  businessId: string,
) {
  try {
    const algoliaResponse = await axios.post(
      `${API_URL}/v1/customers/algolia`,
      { customerId, businessId },
    );

    return algoliaResponse.data.result;
  } catch (error) {
    throw new Error('Cannot add customer to search index', { cause: error });
  }
}

export async function updateCustomer(
  values: CreateCustomerValues,
  customerId: string,
  businessId?: string,
  waitForIndex?: boolean,
) {
  // Filter out fields with empty string values
  const {
    streetAddress,
    streetAddress2,
    city,
    state,
    zip,
    ecFirstName,
    ecLastName,
    ecPhoneNumber,
    ecEmail,
    ...rest
  } = values;

  const filteredValues = Object.entries(rest).reduce(
    (acc: Partial<CreateCustomerValues>, [key, value]) => {
      const allowEmptyString = ['lastName', 'internalNotes'];
      if (value !== '' || allowEmptyString.includes(key)) {
        acc[key as keyof CreateCustomerValues] = value;
      }
      return acc;
    },
    {} as Partial<CreateCustomerValues>,
  );

  const phoneNumber =
    filteredValues.phoneNumber?.charAt(0) === '+'
      ? filteredValues.phoneNumber
      : `+${filteredValues.phoneNumber}`;

  const hasAddress = [streetAddress, city, state, zip].some(v => v);
  const address = hasAddress
    ? { streetAddress, streetAddress2, city, state, zip }
    : undefined;

  const hasEmergencyContact = [
    ecFirstName,
    ecLastName,
    ecPhoneNumber,
    ecEmail,
  ].some(v => v);
  const emergencyContact = hasEmergencyContact
    ? {
        ...(ecFirstName && { firstName: ecFirstName }),
        ...(ecLastName && { lastName: ecLastName }),
        ...(ecPhoneNumber && { phoneNumber: ecPhoneNumber }),
        ...(ecEmail && { email: ecEmail }),
      }
    : undefined;

  if (
    emergencyContact?.phoneNumber &&
    !emergencyContact?.phoneNumber.startsWith('+')
  ) {
    emergencyContact.phoneNumber = `+${emergencyContact.phoneNumber}`;
  }

  const customer = await axios.patch(`${API_URL}/v1/customers/${customerId}`, {
    ...filteredValues,
    phoneNumber,
    businessId,
    waitForIndex,
    ...(address && { address }),
    ...(emergencyContact && { emergencyContact }),
  });

  return customer.data.result;
}

export async function getRoleByPhoneNumber(phoneNumber: string) {
  try {
    const role = await axios.get(`${API_URL}/v1/users/role/${phoneNumber}`);

    return role.data.result as Roles | undefined;
  } catch {
    return undefined;
  }
}

export async function refetchCustomerPayments(customerId: string) {
  const key = ['customer-payments', customerId];
  await client.refetchQueries({ queryKey: key, exact: false });
}

export async function invalidateCustomerPayments(customerId: string) {
  const key = ['customer-payments', customerId];
  await client.invalidateQueries({ queryKey: key, exact: false });
}
